import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui';
import styles from './HeroSection.module.css';
import type { HeroSectionProps } from './HeroSection.types';

const STEP_2_IMAGE = '/images/thumbnails/hero/pet-dog-tag-classic-portrait.png';
const STEP_3_IMAGE = '/images/thumbnails/hero/engraved-pet-dog-tag.png';
const STEP_3_VIDEO = '/images/hero/engraved-pet-dog-tag.mp4';

// ---- Slideshow data ----

type SegmentKind = 'text' | 'filled' | 'empty';
interface Segment {
  readonly kind: SegmentKind;
  readonly text: string;
}
interface HeroSlide {
  readonly image: string;
  readonly alt: string;
  readonly segments: readonly Segment[];
}

/** Default mad-lib shown when slideshow is inactive (pet dog tag classic portrait). */
const DEFAULT_SEGMENTS: readonly Segment[] = [
  { kind: 'text', text: 'Pet portrait, ' },
  { kind: 'filled', text: 'cross-hatching' },
  { kind: 'text', text: ' style, ' },
  { kind: 'empty', text: 'vibe' },
  { kind: 'text', text: ' mood. Pet name: ' },
  { kind: 'filled', text: 'Layla 555-123-4567' },
];

/** Template slides cycled on hover. Each matches a real template prompt. */
const HERO_SLIDES: readonly HeroSlide[] = [
  {
    image: '/images/templates/dog-tag/pet-dog-tag-decorative.png',
    alt: 'Decorative pet dog tag design',
    segments: [
      { kind: 'text', text: 'Pet design, ' },
      { kind: 'filled', text: 'botanical' },
      { kind: 'text', text: ' style, ' },
      { kind: 'empty', text: 'vibe' },
      { kind: 'text', text: ' mood. Pet: ' },
      { kind: 'filled', text: 'Layla 555-123-4567' },
    ],
  },
  {
    image: '/images/templates/dog-tag/custom-prompt.png',
    alt: 'Custom prompt design',
    segments: [
      { kind: 'text', text: 'Your own design. ' },
      { kind: 'empty', text: 'art style' },
      { kind: 'text', text: ' style, ' },
      { kind: 'empty', text: 'vibe' },
      { kind: 'text', text: ' mood' },
    ],
  },
  {
    image: '/images/templates/dog-tag/custom-prompt-celtic-tree.png',
    alt: 'Celtic tree of life design',
    segments: [
      { kind: 'text', text: 'Celtic tree of life. ' },
      { kind: 'filled', text: 'fine-line' },
      { kind: 'text', text: ' style, ' },
      { kind: 'empty', text: 'vibe' },
      { kind: 'text', text: ' mood' },
    ],
  },
  {
    image: '/images/templates/dog-tag/emblem-banner-style.png',
    alt: 'Emblem banner style design',
    segments: [
      { kind: 'text', text: 'Ribbon banner, ' },
      { kind: 'filled', text: 'botanical' },
      { kind: 'text', text: ' style, ' },
      { kind: 'empty', text: 'vibe' },
      { kind: 'text', text: ' mood. Name: ' },
      { kind: 'filled', text: 'FAMILY NAME' },
    ],
  },
  {
    image: '/images/templates/dog-tag/emblem-circular-seal.png',
    alt: 'Circular seal emblem design',
    segments: [
      { kind: 'text', text: 'Round seal, ' },
      { kind: 'empty', text: 'art style' },
      { kind: 'text', text: ' style, ' },
      { kind: 'filled', text: 'solemn' },
      { kind: 'text', text: ' mood. Text: ' },
      { kind: 'filled', text: 'FAMILY NAME' },
    ],
  },
  {
    image: '/images/templates/dog-tag/emblem-heraldic.png',
    alt: 'Heraldic coat of arms design',
    segments: [
      { kind: 'text', text: 'Coat of arms. ' },
      { kind: 'filled', text: 'cross-hatching' },
      { kind: 'text', text: ' style, ' },
      { kind: 'empty', text: 'vibe' },
      { kind: 'text', text: ' mood' },
    ],
  },
  {
    image: '/images/templates/dog-tag/luggage-tag-city-skyline.png',
    alt: 'City skyline luggage tag design',
    segments: [
      { kind: 'text', text: 'City skyline, ' },
      { kind: 'filled', text: 'silhouette' },
      { kind: 'text', text: ' style, ' },
      { kind: 'empty', text: 'vibe' },
      { kind: 'text', text: ' mood. Owner: ' },
      { kind: 'filled', text: 'John Doe' },
    ],
  },
  {
    image: '/images/templates/dog-tag/qr-code-centered-qr-centered.png',
    alt: 'QR code frame design',
    segments: [
      { kind: 'text', text: 'QR code frame. ' },
      { kind: 'filled', text: 'outline' },
      { kind: 'text', text: ' style, ' },
      { kind: 'empty', text: 'vibe' },
      { kind: 'text', text: ' mood. Label: ' },
      { kind: 'filled', text: 'SCAN ME' },
    ],
  },
];

// ---- Sub-components ----

/** Renders a mad-lib sentence from segment data. */
function MadLibSegments({ segments }: { segments: readonly Segment[] }) {
  return (
    <div className={styles.madLibPreview}>
      <p className={styles.madLibSentence}>
        {segments.map((seg, i) => {
          const cls =
            seg.kind === 'filled'
              ? styles.madLibFilled
              : seg.kind === 'empty'
                ? styles.madLibEmpty
                : styles.madLibText;
          return (
            <span key={i} className={cls}>
              {seg.text}
            </span>
          );
        })}
      </p>
    </div>
  );
}

/**
 * Steps 1 & 2 with hover-triggered slideshow.
 * On hover, cycles through template examples every 2 seconds.
 */
function SlideshowSteps() {
  const [slideIndex, setSlideIndex] = useState(-1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startSlideshow = useCallback(() => {
    if (intervalRef.current) return;
    setSlideIndex(0);
    intervalRef.current = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 2000);
  }, []);

  const stopSlideshow = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setSlideIndex(-1);
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Preload slideshow images so transitions are seamless
  useEffect(() => {
    HERO_SLIDES.forEach((slide) => {
      const img = new Image();
      img.src = slide.image;
    });
  }, []);

  const active = slideIndex >= 0;
  const slide = active ? HERO_SLIDES[slideIndex] : null;

  return (
    <div
      className={styles.slideshowZone}
      onMouseEnter={startSlideshow}
      onMouseLeave={stopSlideshow}
      onTouchStart={startSlideshow}
      onTouchEnd={stopSlideshow}
    >
      {/* Step 1: Describe */}
      <div className={styles.step}>
        <div className={styles.stepNumber}>1</div>
        <div className={styles.promptBubble}>
          <div
            key={active ? slideIndex : 'default'}
            className={active ? styles.slideFadeIn : undefined}
          >
            <MadLibSegments segments={active ? slide!.segments : DEFAULT_SEGMENTS} />
          </div>
        </div>
        <span className={styles.stepLabel}>Design Input</span>
      </div>

      <div className={styles.arrow} aria-hidden="true">
        &rarr;
      </div>

      {/* Step 2: AI Creates */}
      <div className={styles.step}>
        <div className={styles.stepNumber}>2</div>
        <div className={styles.stepImageWrapper}>
          <img
            key={active ? slideIndex : 'default'}
            src={active ? slide!.image : STEP_2_IMAGE}
            alt={active ? slide!.alt : 'AI-generated pet dog tag design'}
            className={`${styles.stepImage} ${active ? `${styles.slideshowImage} ${styles.slideFadeIn}` : ''}`}
            loading="eager"
          />
        </div>
        <span className={styles.stepLabel}>AI Generates Design</span>
      </div>
    </div>
  );
}

/** Step 3 card: shows static image, plays video on hover/touch. */
function EngravedStep() {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleEnter = useCallback(() => {
    videoRef.current?.play().catch(() => {});
  }, []);

  const handleLeave = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  }, []);

  return (
    <div className={styles.step}>
      <div className={styles.stepNumber}>3</div>
      <div
        className={styles.stepImageWrapper}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onTouchStart={handleEnter}
        onTouchEnd={handleLeave}
      >
        <img
          src={STEP_3_IMAGE}
          alt="Engraved pet dog tag with Layla"
          className={`${styles.stepImage} ${styles.posterImage}`}
        />
        <video
          ref={videoRef}
          src={STEP_3_VIDEO}
          muted
          loop
          playsInline
          preload="auto"
          className={`${styles.stepImage} ${styles.hoverVideo}`}
          aria-hidden="true"
        />
      </div>
      <span className={styles.stepLabel}>Engraved & Shipped</span>
    </div>
  );
}

/**
 * HeroSection - AI before/after showcase with a clear CTA.
 *
 * Shows a 3-step visual flow: Describe → AI Creates → Engraved,
 * demonstrating the core AI generation value proposition.
 * Steps 1 & 2 feature a hover-triggered slideshow cycling through
 * different template examples.
 */
export function HeroSection({
  headline = 'Made to Order Laser Engraving',
  subtext = 'Describe your idea, and our AI turns it into a custom engraved product, or manually design the engraving',
  className,
}: HeroSectionProps) {
  const classNames = [styles.hero, className].filter(Boolean).join(' ');

  const handleScrollToProducts = useCallback(() => {
    const productsSection = document.getElementById('product-grid');
    productsSection?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <section className={classNames} aria-label="Welcome">
      <h2 className={styles.headline}>{headline}</h2>
      <p className={styles.subtext}>{subtext}</p>

      <div className={styles.showcase} aria-label="How it works">
        <SlideshowSteps />

        <div className={styles.arrow} aria-hidden="true">
          &rarr;
        </div>

        <EngravedStep />
      </div>

      <div className={styles.ctaArea}>
        <Button variant="primary" size="lg" onClick={handleScrollToProducts}>
          Choose a Product
        </Button>
        <span className={styles.scrollHint} aria-hidden="true">
          <ChevronDown size={14} />
          See products below
        </span>
      </div>
    </section>
  );
}
